import { useAtomValue } from 'jotai';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

import { getSiteSpacesFromStructure, lastViewedSitesSelector } from '../../state';

/**
 * Sort options available for organization site lists and switchers.
 */
export enum SiteSortBy {
    Viewed = 'viewed',
    Updated = 'updated',
    Created = 'created',
}

/**
 * Default sort used for organization site surfaces.
 */
export const DEFAULT_SITE_SORT_BY = SiteSortBy.Viewed;

/**
 * Sort the given sites list according to the selected sort option.
 */
export function useSortSites(input: {
    allSites: api.OrganizationAllSite[];
    sortBy: SiteSortBy;
    organizationId: string;
    limit?: number;
}): {
    sortedSites: Array<{
        item: api.OrganizationAllSite;
        modifiedDate: ReturnType<typeof getSiteModifiedDate>;
    }>;
    // returns `hasAnyViewedSite` so callers can hide the "My recent sites"
    // option until the user has actually visited a site.
    hasAnyViewedSite: boolean;
} {
    const { allSites, sortBy, organizationId, limit } = input;

    const lastViewedSites = useAtomValue(lastViewedSitesSelector(organizationId));
    const hasAnyViewedSite = Object.keys(lastViewedSites).length > 0;

    // Sorting defaults to "last viewed" if data is available and falls back to "last updated".
    const effectiveSortBy =
        sortBy === SiteSortBy.Viewed && !hasAnyViewedSite ? FALLBACK_SORT_BY : sortBy;

    const decorated = React.useMemo(
        () =>
            allSites.map((item) => ({
                item,
                modifiedDate: getSiteModifiedDate(item),
            })),
        [allSites]
    );

    const sortedSites = React.useMemo(() => {
        const sortDecoratedSites = () => [...decorated];

        const applyLimit = (sorted: typeof decorated) =>
            typeof limit === 'number' ? sorted.slice(0, limit) : sorted;

        switch (effectiveSortBy) {
            case SiteSortBy.Viewed:
                return applyLimit(
                    sortDecoratedSites().sort((a, b) => {
                        const aViewed = lastViewedSites[a.item.site.id] ?? 0;
                        const bViewed = lastViewedSites[b.item.site.id] ?? 0;
                        if (aViewed !== bViewed) {
                            return bViewed - aViewed;
                        }
                        // Fall back to modified date for sites that were never viewed.
                        return b.modifiedDate.date.getTime() - a.modifiedDate.date.getTime();
                    })
                );
            case SiteSortBy.Updated:
                return applyLimit(
                    sortDecoratedSites().sort(
                        (a, b) => b.modifiedDate.date.getTime() - a.modifiedDate.date.getTime()
                    )
                );
            case SiteSortBy.Created:
                return applyLimit(
                    sortDecoratedSites().sort(
                        (a, b) =>
                            new Date(b.item.site.createdAt).getTime() -
                            new Date(a.item.site.createdAt).getTime()
                    )
                );
            default:
                assertNever(effectiveSortBy);
        }
    }, [decorated, effectiveSortBy, lastViewedSites, limit]);

    return { sortedSites, hasAnyViewedSite };
}

/**
 * Fallback sort used when the selected option has no data yet.
 */
const FALLBACK_SORT_BY = SiteSortBy.Updated;

/**
 * Returns the date of the most recent update on a site's content.
 * Falls back to the site creation date if it was never updated.
 */
function getSiteModifiedDate(item: api.OrganizationAllSite) {
    let modifiedDate: {
        date: Date;
        type: 'created' | 'updated';
    } = { date: new Date(item.site.createdAt), type: 'created' };

    getSiteSpacesFromStructure(item.structure).forEach((siteSpace) => {
        const siteSpaceUpdatedAt = new Date(siteSpace.space.updatedAt);
        if (siteSpaceUpdatedAt.getTime() > modifiedDate.date.getTime()) {
            modifiedDate = { date: siteSpaceUpdatedAt, type: 'updated' };
        }
    });

    return modifiedDate;
}
